<template>
    <div
        class="container-fluid w-100 h-100"
        style="overflow-x: hidden"
    >
        <!-- Subscription Banner -->
        <subscription-banner
            v-if="workflowStep === 'setup'"
            :subscription="subscription"
        />

        <ul
            v-if="workflowStep === 'setup'"
            class="nav nav-tabs routa-tabs"
        >
            <li class="nav-item">
                <button
                    class="routa-tab"
                    :class="{ active: tabIndex === 0 }"
                    @click="tabIndex = 0"
                >
                    {{ $t('route_optimization.setup_tab') }}
                </button>
            </li>
            <li class="nav-item">
                <button
                    class="routa-tab"
                    :class="{ active: tabIndex === 1 }"
                    @click="tabIndex = 1"
                >
                    {{ $t('route_optimization.jobs_tab') }}
                </button>
            </li>
        </ul>

        <div
            v-if="workflowStep === 'setup'"
            class="tab-content routa-tab-content"
        >
            <div
                class="tab-pane fade"
                :class="{ show: tabIndex === 0, active: tabIndex === 0 }"
                style="min-height: 75vh"
            >
                <keep-alive>
                    <route-optimization-setup
                        ref="setupComponent"
                        :opt="opt"
                        :has-simulation="hasSimulation"
                        @update:opt="opt = $event"
                        @go-to-simulation="goToSimulation"
                    />
                </keep-alive>
            </div>
            <div
                class="tab-pane fade"
                :class="{ show: tabIndex === 1, active: tabIndex === 1 }"
                style="min-height: 75vh"
            >
                <route-optimization-jobs
                    ref="jobsComponent"
                    :is-observer="isObserver"
                    @view-simulation="goToSimulation(true)"
                    @rerun-setup="goToRerun"
                />
            </div>
        </div>

        <route-optimization-simulation
            v-if="workflowStep === 'simulation'"
            ref="simulationComponent"
            @go-back="goBackToSetup"
        />
    </div>
</template>

<script>
import RouteOptimizationSetup from './RouteOptimizationSetup.vue'
import SubscriptionBanner from './SubscriptionBanner.vue'
import { restApi } from '../mixins/RestApiMixin'
import { userHelper } from '../mixins/UserMixin'
import RouteOptimizationSimulation from "@/components/routeoptimization/RouteOptimizationSimulation.vue";
import { routeOptimizationMixin } from '../mixins/RouteOptimizationMixin';
import RouteOptimizationJobs from './RouteOptimizationJobs.vue';

export default {
    name: "RouteOptimizationWorkflow",
    components: {
        RouteOptimizationSimulation,
        RouteOptimizationSetup,
        SubscriptionBanner,
        RouteOptimizationJobs
    },
    mixins: [restApi, routeOptimizationMixin, userHelper],
    props: {
        user: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            opt: {},
            hasSimulation: false,
            startedFromSetup: false,
            tabIndex: 0
        };
    },
    computed: {
        isObserver() {
            return !!(this.user && this.user.roles && this.hasObserver(this.user.roles))
        },
        subscription() {
            return this.$store.state.subscriptions.subscription;
        },
        routeOptimizationResult() {
            return this.$store.state.routeOptimization.routeOptimizationResult;
        },
        optimizerLoading() {
            return this.$store.state.routeOptimization.optimizerLoading;
        }
    },

    watch: {
        routeOptimizationResult(newResult) {
            this.hasSimulation = !!(newResult && newResult.length > 0);
            if (this.hasSimulation && this.workflowStep === 'setup') {
                this.startedFromSetup = true;
                this.workflowStep = 'simulation';
            }
        },
        optimizerLoading(val, oldVal) {
            // Refresh jobs list when optimization finishes (success or failure)
            if (oldVal === true && val === false) {
                this.$refs.jobsComponent?.refresh();
            }
        },
        tabIndex(newIndex, oldIndex) {
            // Refresh jobs list when switching to jobs tab
            if (newIndex === 1 && oldIndex === 0) {
                this.$nextTick(() => {
                    this.$refs.jobsComponent?.refresh();
                });
            }
        }
    },

    mounted() {
        const result = this.$store.state.routeOptimization.routeOptimizationResult;
        if (result && result.length > 0) {
            this.hasSimulation = true;
        }
    },

    methods: {
        goBackToSetup() {
            // Preserve scroll position
            const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

            if (!this.startedFromSetup) {
                this.$store.dispatch('routeOptimization/resetSetupState');
                this.hasSimulation = false;
                if (this.$refs.setupComponent) {
                    this.$refs.setupComponent.resetOptimizationState();
                }
            }
            this.startedFromSetup = false;
            this.workflowStep = 'setup';

            this.$nextTick(() => {
                // Restore scroll position after re-render
                window.scrollTo(0, scrollPosition);

                const setup = this.$refs.setupComponent;
                if (setup && setup.previewMap) {
                    setup.renderGeometry();
                }
            });
        },

        goToRerun() {
            // Clear the previous successful simulation so the setup view shows
            // "Optimize" instead of "Show simulation" when re-running a failed job
            this.hasSimulation = false;
            this.$store.commit('routeOptimization/storeRouteOptimizationResult', null);
            this.workflowStep = 'setup';
            this.tabIndex = 0;
            this.$nextTick(() => {
                const setup = this.$refs.setupComponent;
                if (setup) {
                    // Sync the input values with store values
                    setup.maxDurationHoursInput = this.$store.state.routeOptimization.maxDurationHours;
                    setup.maxDurationMinutesInput = this.$store.state.routeOptimization.maxDurationMinutes;
                    setup.optimizationName = this.$store.state.routeOptimization.optimizationName || '';

                    // Invalidate map size after tab becomes visible, then render
                    setTimeout(() => {
                        if (setup.previewMap) {
                            setup.previewMap.resizeMap();
                        }
                        setup.renderGeometry();
                    }, 100);

                    // Force update to ensure UI reflects the values
                    setup.$forceUpdate();
                }
            });
        },

        goToSimulation(fromJobs = false) {
            this.hasSimulation = !!(this.$store.state.routeOptimization.routeOptimizationResult?.length > 0);
            if (this.hasSimulation) {
                this.startedFromSetup = !fromJobs;
                this.tabIndex = 0;
                this.workflowStep = 'simulation';
            }
        }
    }
}
</script>

<style scoped>
</style>
